import axiosInstance from '../auth/axiosInstance';

/** Fetches the raw assessment payload used as AI report input (debug / inspection). */
export const getAiProjectReportData = async ({ projectId }) => {
  const { data } = await axiosInstance.get('/AiReport/getProjectReportData', {
    params: { projectId },
  });

  return data;
};

export const getAxlCompetitors = async (ticker) => {
  const { data } = await axiosInstance.get('/AiReport/competitors', {
    params: { ticker },
  });

  return data?.competitors || {};
};

export type StockCompanySearchHit = { id: string; n: string };

/** Server-side proxy — stockanalysis.com blocks browser cross-origin requests. */
export const searchStockCompanies = async (query: string): Promise<StockCompanySearchHit[]> => {
  const { data } = await axiosInstance.get('/AiReport/companySearch', {
    params: { q: query.trim() },
  });

  if (!Array.isArray(data?.data)) {
    return [];
  }

  return data.data
    .map((row: { id?: string; n?: string }) => ({
      id: String(row.id ?? '').trim(),
      n: String(row.n ?? '').trim(),
    }))
    .filter((row: StockCompanySearchHit) => row.id && row.n);
};

/**
 * Generates the AI capability assessment report PDF.
 * Backend loads project data, calls Traxccel AI generate, converts HTML → PDF.
 */
export const isReportGenerationRequestAborted = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') {
    return false;
  }
  const record = error as { code?: string; name?: string; message?: string };
  if (record.code === 'ERR_CANCELED' || record.name === 'CanceledError' || record.name === 'AbortError') {
    return true;
  }
  const message = String(record.message || '').toLowerCase();
  return message.includes('abort') || message.includes('cancel');
};

export const generateAiProjectReportPdf = async ({
  projectId,
  marketIntelligence,
  editable = false,
  signal,
}: {
  projectId: number | string;
  marketIntelligence?: Record<string, unknown>;
  editable?: boolean;
  signal?: AbortSignal;
}) => {
  try {
    const response = await axiosInstance.post('/AiReport/generateProjectReport', marketIntelligence || {}, {
      params: { projectId, editable: editable ? true : false },
      responseType: 'blob',
      timeout: 0,
      signal,
    });

    const contentType = response.headers['content-type'] || '';
    if (contentType.includes('application/json') || contentType.includes('text/plain')) {
      const text = await response.data.text();
      throw new Error(await parseErrorText(text));
    }

    const disposition = response.headers['content-disposition'] || '';
    const fileNameMatch = /filename\*?=(?:UTF-8''|")?([^\";]+)/i.exec(disposition);
    const fileName = fileNameMatch
      ? decodeURIComponent(fileNameMatch[1].replace(/"/g, '').trim())
      : 'project-report.pdf';

    const sourceHtmlPath =
      response.headers['x-report-source-html'] ||
      response.headers['X-Report-Source-Html'] ||
      '';

    return { blob: response.data, fileName, sourceHtmlPath: sourceHtmlPath || null };
  } catch (error) {
    if (isReportGenerationRequestAborted(error)) {
      throw error;
    }
    if (error?.response?.data instanceof Blob) {
      const text = await error.response.data.text();
      throw new Error(await parseErrorText(text));
    }
    throw error;
  }
};

async function parseErrorText(text) {
  if (!text) {
    return 'Failed to generate project report.';
  }
  try {
    const parsed = JSON.parse(text);
    return (
      parsed?.message ||
      parsed?.title ||
      parsed?.error ||
      (typeof parsed === 'string' ? parsed : text)
    );
  } catch {
    return text;
  }
}
